import { numberToCurrency } from 'core/helpers'
import { V1ApiDealersClient } from 'core/http-common'
import { useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { ICustomColumn, IGridLocalStorageNames } from 'types'
import toast from 'core/toast'
import { DealerBillingInformationDto, DealerInvoiceTotalsDto, ProductTypeEnum } from '@v1DealersApi'
import SAGrid, { GetDataResponse, GridGetDataProps } from 'components/Grid/SAGrid'
import { GridCellProps } from '@progress/kendo-react-grid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface IProps {
    editable: boolean
    formIsModified: boolean
    dealerId?: number
    productType: ProductTypeEnum
}

const LOCAL_STORAGE_KEYS = {
    CASES: {
        SORT: 'caseProductsSort',
        SEARCH: 'caseProductsSearch',
        PAGE_SIZE: 'caseProductsPageSize',
        SELECTED_PAGE: 'caseProductsSelectedPage'
    },
    DEALERS: {
        SORT: 'dealerProductsSort',
        SEARCH: 'dealerProductsSearch',
        PAGE_SIZE: 'dealerProductsPageSize',
        SELECTED_PAGE: 'dealerProductsSelectedPage'
    }
}

const ProductsForm = ({ editable, dealerId, productType }: IProps) => {

    const [paymentMethod, setPaymentMethod] = useState<string>('')

    const [billingInformation, setBillingInformation] = useState<DealerBillingInformationDto>()
    const [openInvoiceTotals, setOpenInvoiceTotals] = useState<DealerInvoiceTotalsDto>()
    const [totalProductCost, setTotalProductCost] = useState<number>()

    let gridLocalStorageNames: IGridLocalStorageNames = {
        sort: productType == ProductTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.SORT : LOCAL_STORAGE_KEYS.CASES.SORT,
        search: productType == ProductTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.SEARCH : LOCAL_STORAGE_KEYS.CASES.SEARCH,
        selectedPage: productType == ProductTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.SELECTED_PAGE : LOCAL_STORAGE_KEYS.CASES.SELECTED_PAGE,
        pageSize: productType == ProductTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.PAGE_SIZE : LOCAL_STORAGE_KEYS.CASES.PAGE_SIZE,
    }

    // custom cells will be used when product dealer table gets populated
    const MonthlyCell = (props: GridCellProps) => {

        if (props.dataItem.monthly == true) {

            return (
                <td className='dealers-monthly-cell text-center'>
                    <FontAwesomeIcon icon={['fas', 'check']} />
                </td>
            )
        } else {

            return (
                <td className='dealers-monthly-cell text-center'>
                </td>
            )
        }
    }

    // custom cells will be used when product dealer table gets populated
    const YearlyCell = (props: GridCellProps) => {

        if (props.dataItem.annually == true) {

            return (
                <td className='dealers-yearly-cell text-center'>
                    <FontAwesomeIcon icon={['fas', 'check']} />
                </td>
            )
        } else {

            return (
                <td className='dealers-yearly-cell text-center'>
                </td>
            )
        }
    }

    // custom cells will be used when product dealer table gets populated
    const QuarterlyCell = (props: GridCellProps) => {

        if (props.dataItem.quarterly == true) {

            return (
                <td className='dealers-quarterly-cell text-center'>
                    <FontAwesomeIcon icon={['fas', 'check']} />
                </td>
            )
        } else {

            return (
                <td className='dealers-quarterly-cell text-center'>
                </td>
            )
        }
    }

    const productColumns: ICustomColumn[] = [
        { field: 'productCost', title: 'Cost', format: '{0:C0}', width: '95px' },
        { field: 'productCode', title: 'Code', width: '125px' },
        { field: 'productName', title: 'Product Name', width: '350px' },
        { field: 'productOwnerName', title: 'Owner' },
        { field: 'status', title: 'Status', width: '95px' },
        // custom cells will be used when product dealer table gets populated
        // { field: 'monthly', title: 'Monthly', cell: MonthlyCell, headerClassName: 'dealers-monthly-header', width: '85px' },
        // { field: 'quarterly', title: 'Quarterly', cell: QuarterlyCell, headerClassName: 'dealers-quarterly-header', width: '90px' },
        // { field: 'annually', title: 'Annually', cell: YearlyCell, headerClassName: 'dealers-yearly-header', width: '85px' },
        { field: 'createdDate', title: 'Created Date', format: '{0:d}', filterType: 'date' },
        { field: 'contractEndDate', title: 'Contract End Date', format: '{0:d}', filterType: 'date' },
        { field: 'savedDate', title: 'Saved Date', format: '{0:d}', filterType: 'date' },
        { field: 'deletionDate', title: 'Delete Date', format: '{0:d}', filterType: 'date', display: productType == ProductTypeEnum.Dealer ? true : false },
    ]

    const getData = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        try {

            const result = await V1ApiDealersClient.getProducts(
                dealerId!,
                productType,
                props.search,
                props.sortBy,
                props.sortAscending,
                props.paging && props.paging.currentPage ? props.paging.currentPage : 1,
                props.paging ? props.paging.take : 10,
                true,
                true
            )

            setTotalProductCost(result.totalProductCost?.totalProductCost)

            if (result.products) {
                return {
                    data: result.products?.data,
                    total: result.products.totalItems ? result.products.totalItems : 0
                }
            } else {
                return {
                    data: [],
                    total: 0
                }
            }
        }
        catch (e) {
            toast.responseErrors(e)
            return {
                data: [],
                total: 0
            }

        }
    }

    const fetch = async () => {
        if (!dealerId || dealerId <= 0)
            return

        try {

            const [
                openInvoiceTotals,
                billingInformation,
            ] = await Promise.all([
                V1ApiDealersClient.getOpenInvoiceTotals(dealerId),
                V1ApiDealersClient.getBillingInformation(dealerId),
            ])
            setBillingInformation(billingInformation)
            setOpenInvoiceTotals(openInvoiceTotals)
            const paymentType = billingInformation?.invoiceTypeId
            if (paymentType) {
                if (paymentType === 1) {
                    setPaymentMethod('Credit Card')
                } else if (paymentType === 2) {
                    setPaymentMethod('ACH')
                } else if (paymentType === 3) {
                    setPaymentMethod('Mail')
                }
            }

        }
        catch (e) {
            toast.responseErrors(e)
        }
    }

    useEffect(() => {
        fetch()
    }, [dealerId])

    return (
        <div id='ProductsForm'>
            <div className='bg-light shadow-sm p-3 border pb-5'>
                <Row>
                    <Col md={6}><h4>Product Information</h4></Col>
                    <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                        <b>Recurring Product Summary:</b> (Billed on: {billingInformation?.billDay} by {paymentMethod})
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Col md={4}>
                        <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2'>
                            <Row>
                                <Col><b>Open Invoice Total:</b></Col>
                                <Col className='text-end col-auto '>{numberToCurrency(openInvoiceTotals?.openInvoiceTotal)}</Col>
                            </Row>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2'>
                            <Row>
                                <Col><b>Past Due Invoice Total:</b></Col>
                                <Col className='col-auto text-end'>{numberToCurrency(openInvoiceTotals?.pastDueInvoiceTotal)}</Col>
                            </Row>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2'>
                            <Row>
                                <Col><b>Monthly Recurring Revenue:</b></Col>
                                <Col className='text-end col-auto '>{numberToCurrency(totalProductCost ?? 0)}</Col>
                            </Row>
                        </div>
                    </Col>
                    <Col xs={12}>
                        {dealerId
                            && dealerId > 0
                            &&
                            <SAGrid
                                features={
                                    {
                                        pageable: true,
                                        sortable: true,
                                        simpleSearch: true,
                                    }
                                }
                                columns={productColumns}
                                defaultSort={[{ field: 'createdDate', dir: 'asc' }]}
                                getData={getData}
                                localStorageNames={gridLocalStorageNames}
                            />
                        }
                    </Col>
                </Row>
            </div>
        </div>
    )
}
export default ProductsForm